import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { SEO } from "@/components/SEO";
import { BlogContent, EditorsPicks } from "@/components/BlogProducts";
import { CalendarBlank, User, ArrowRight } from "@phosphor-icons/react";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso.slice(0, 10);
  }
}

export function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [params, setParams] = useSearchParams();
  const active = params.get("section") || "";
  const sections = [...new Set(posts.map((p) => p.section).filter(Boolean))];
  const shown = active ? posts.filter((p) => p.section === active) : posts;
  useEffect(() => {
    api.get("/posts", { params: { published: true } })
      .then((r) => setPosts(r.data))
      .catch((err) => console.error("Failed to load posts", err));
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
      <SEO
        title="Blog"
        description="Editorial notes, buying guides, and behind-the-scenes stories from the HomeHearth team."
        path="/blog"
      />
      <p className="text-xs uppercase tracking-[0.2em] text-[#5B7B53] mb-3">Journal</p>
      <h1 className="font-display text-5xl sm:text-6xl text-[#2C2B29] mb-4 leading-tight">Notes from our kitchen.</h1>
      <p className="text-lg text-[#5C5A56] max-w-2xl mb-12">
        Long-form essays, buying frameworks, and behind-the-scenes stories — the kind of thing that doesn't fit in a product review.
      </p>

      {sections.length > 0 && (
        <div data-testid="blog-section-filter" className="flex flex-wrap gap-2 mb-10">
          {["", ...sections].map((sec) => (
            <button
              key={sec || "all"}
              data-testid={sec ? `blog-section-${sec.toLowerCase().replace(/[^a-z]+/g, "-")}` : "blog-section-all"}
              onClick={() => setParams(sec ? { section: sec } : {})}
              className={`px-4 py-2 rounded-full text-sm border transition-colors duration-200 ${active === sec ? "bg-[#5B7B53] text-white border-[#5B7B53]" : "border-[#E5E0D8] text-[#2C2B29] hover:border-[#5B7B53]"}`}
            >
              {sec || "All articles"}
            </button>
          ))}
        </div>
      )}
      {active && <h2 data-testid="blog-section-heading" className="font-display text-3xl text-[#2C2B29] mb-8">{active}</h2>}

      {shown.length === 0 ? (
        <p className="text-[#5C5A56]">No posts yet.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-10">
          {shown.map((p, idx) => (
            <Link
              key={p.id}
              to={`/blog/${p.id}`}
              data-testid={`blog-tile-${p.id}`}
              className={`group ${idx === 0 ? "md:col-span-2" : ""}`}
            >
              <div className={`overflow-hidden rounded-3xl mb-5 ${idx === 0 ? "aspect-[21/9]" : "aspect-[16/10]"}`}>
                {p.cover_image ? (
                  <img
                    src={p.cover_image}
                    alt={p.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                  />
                ) : (
                  <div className="w-full h-full bg-[#EADDC8]" />
                )}
              </div>
              {p.section && <p className="text-[11px] uppercase tracking-[0.18em] text-[#D17A58] font-semibold mb-2">{p.section}</p>}
              <div className="flex items-center gap-3 text-xs uppercase tracking-wider text-[#5B7B53] mb-3">
                <span className="flex items-center gap-1.5"><User size={14} weight="duotone" /> {p.author}</span>
                <span className="flex items-center gap-1.5"><CalendarBlank size={14} weight="duotone" /> {formatDate(p.created_at)}</span>
              </div>
              <h2 className={`font-display text-[#2C2B29] leading-tight group-hover:text-[#5B7B53] transition-colors ${idx === 0 ? "text-4xl" : "text-2xl"}`}>
                {p.title}
              </h2>
              {p.subtitle ? <p className="text-[#5C5A56] mt-3">{p.subtitle}</p> : null}
              <span className="inline-flex items-center gap-1.5 mt-4 text-sm font-medium text-[#D17A58]">
                Read more <ArrowRight size={14} weight="bold" />
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function BlogDetailPage() {
  const { id } = useParams();
  const [post, setPost] = useState(null);

  useEffect(() => {
    api.get(`/posts/${id}`).then((r) => setPost(r.data)).catch(() => setPost(false));
  }, [id]);

  if (post === false) return <div className="max-w-3xl mx-auto p-10 text-[#5C5A56]">Post not found.</div>;
  if (!post) return <div className="max-w-3xl mx-auto p-10 text-[#5C5A56]">Loading...</div>;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.subtitle,
    image: post.cover_image,
    author: { "@type": "Person", name: post.author },
    datePublished: post.created_at,
    publisher: { "@type": "Organization", name: "HomeHearth" },
  };

  return (
    <article>
      <SEO
        title={post.title}
        description={post.subtitle || post.content?.replace(/\[(product|compare):[a-fA-F0-9,\s]+\]/g, "").replace(/\*\*/g, "").slice(0, 155)}
        image={post.cover_image}
        path={`/blog/${post.id}`}
        type="article"
        jsonLd={jsonLd}
      />

      <section className="relative overflow-hidden">
        {post.cover_image ? (
          <div className="absolute inset-0">
            <img src={post.cover_image} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#2C2B29]/30 via-[#2C2B29]/70 to-[#2C2B29]/95" />
          </div>
        ) : (
          <div className="absolute inset-0 bg-[#2C2B29]" />
        )}
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 pt-20 pb-14 sm:pt-32 sm:pb-24 text-white">
          {post.section ? (
            <Link data-testid="blog-section-link" to={`/blog?section=${encodeURIComponent(post.section)}`} className="inline-block text-xs uppercase tracking-[0.2em] text-[#EADDC8] mb-4 hover:text-white">{post.section}</Link>
          ) : (
            <p className="text-xs uppercase tracking-[0.2em] text-[#EADDC8] mb-4">Journal</p>
          )}
          <h1 data-testid="blog-title" className="font-display text-[2rem] sm:text-6xl leading-[1.05] tracking-tight">
            {post.title}
          </h1>
          {post.subtitle ? <p className="mt-6 text-xl text-[#EADDC8] max-w-2xl">{post.subtitle}</p> : null}
          <div className="mt-8 flex items-center gap-3 text-sm text-[#EADDC8]">
            <span className="flex items-center gap-1.5"><User size={14} weight="duotone" /> {post.author}</span>
            <span>·</span>
            <span className="flex items-center gap-1.5"><CalendarBlank size={14} weight="duotone" /> {formatDate(post.created_at)}</span>
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 lg:px-10 py-10 sm:py-16">
        <BlogContent content={post.content} products={post.products || {}} />
        {post.tags?.length > 0 && (
          <div className="mt-12 pt-8 border-t border-[#E5E0D8] flex flex-wrap gap-2">
            {post.tags.map((t) => (
              <span key={t} className="text-xs uppercase tracking-wider text-[#5B7B53] bg-[#EADDC8]/40 rounded-full px-3 py-1">#{t}</span>
            ))}
          </div>
        )}
      </section>
      <EditorsPicks post={post} />
    </article>
  );
}
